/* eslint-disable no-nested-ternary */
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"
import {
    Box,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import { ChangeEvent, ReactElement, useState } from "react"

import AppButton from "../../components/buttons/appButton"
import AppAlert from "../../components/dataDisplay/alerts/appAlert"
import VerticalKeyValueList from "../../components/dataDisplay/verticalKeyValueList"
import { EVChargerIcon, HeatPumpIcon } from "../../components/icons/icons"
import AppSkeleton from "../../components/loading/skeletons/appSkeleton"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import DisplayIfElse from "../../components/wrappers/displayIfElse"
import useCompanySearch from "../../services/middleware/hooks/companySearch/useCompanySearch"
import {
    GRID_SPACING,
    SEARCH_BAR_ITEM_RADIUS_MULTIPLIER,
} from "../../themes/defaultThemeParams"

const CompanySearch = (): ReactElement => {
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const [inputValue, setInputValue] = useState("")

    const { isSearching, getCompanies, searchResults, error } =
        useCompanySearch()

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setInputValue(event.target.value)
    }

    const handleSearchClick = (): void => {
        getCompanies(inputValue)
    }

    const hasNoResults = searchResults && searchResults.length === 0

    return (
        <Box width="100%" display="flex" justifyContent="center">
            <Stack
                display="flex"
                flexDirection="column"
                alignItems="center"
                height="100%"
                spacing={2}
                width={{ xs: "100%", md: "60%" }}
            >
                <Typography
                    variant={isSmallScreen ? "h4" : "h3"}
                    fontWeight={500}
                    color={theme.palette.text.primary}
                >
                    {" "}
                    Company Search
                </Typography>
                <Typography
                    variant="h6"
                    color={theme.palette.text.secondary}
                    fontWeight={300}
                    align="center"
                >
                    Any installer or manufacturer that has registered their
                    company with Connect Direct will be listed here. Please
                    note, this list does not include installers that had opted
                    to register as a solo installer (the ability to register as
                    a solo installer was removed Dec 2024)
                </Typography>
                <Stack width={{ xs: "100%", md: "90%" }}>
                    <form
                        onSubmit={(event) => {
                            event.preventDefault()
                            handleSearchClick()
                        }}
                    >
                        <TextField
                            placeholder="Enter a company name"
                            margin="normal"
                            variant="outlined"
                            value={inputValue}
                            onChange={handleInputChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <AppButton
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                handleSearchClick()
                                            }}
                                        >
                                            Search
                                        </AppButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius:
                                        SEARCH_BAR_ITEM_RADIUS_MULTIPLIER *
                                        theme.shape.borderRadius,
                                },
                            }}
                        />
                    </form>
                </Stack>

                <DisplayIfElse
                    condition={isSearching}
                    displayIfTrue={
                        <Stack
                            width="100%"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    xs: "flex-start",
                                    md: "center",
                                },
                                maxHeight: "100%",
                            }}
                            spacing={GRID_SPACING}
                        >
                            <AppSkeleton
                                height={200}
                                width="100%"
                                variant="rectangular"
                            />
                            <AppSkeleton
                                height={200}
                                width="100%"
                                variant="rectangular"
                            />
                            <AppSkeleton
                                height={200}
                                width="100%"
                                variant="rectangular"
                            />
                        </Stack>
                    }
                    displayIfFalse={
                        <DisplayIfElse
                            condition={(searchResults?.length ?? 0) < 1}
                            displayIfTrue={
                                error ? (
                                    <AppAlert severity="warning">
                                        {error.displayDetail}
                                    </AppAlert>
                                ) : hasNoResults ? (
                                    <Typography
                                        variant="h6"
                                        color={theme.palette.text.secondary}
                                        align="center"
                                        marginTop={1}
                                    >
                                        No results found. Please try a different
                                        search term. Please note this search
                                        only returns companies that have
                                        registered with Connect Direct as
                                        companies. Solo installers are not
                                        listed here
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="h6"
                                        color={theme.palette.text.secondary}
                                        align="center"
                                    >
                                        Get started by searching for a company.
                                        You can search by entering a company
                                        name above
                                    </Typography>
                                )
                            }
                            displayIfFalse={
                                <>
                                    <Stack
                                        width="100%"
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: {
                                                xs: "flex-start",
                                                md: "center",
                                            },
                                            maxHeight: "100%",
                                        }}
                                        spacing={GRID_SPACING}
                                    >
                                        {searchResults?.map((company) => (
                                            <AppCard
                                                key={company.organisationName}
                                                sx={{ minWidth: "100%" }}
                                            >
                                                <AppCardHeader
                                                    title={
                                                        company.organisationName
                                                    }
                                                    subheader={
                                                        company.organisationType ===
                                                        "installer"
                                                            ? "Installer Company"
                                                            : "Manufacturer Company"
                                                    }
                                                    avatar={
                                                        company.organisationType ===
                                                        "installer" ? (
                                                            <EVChargerIcon />
                                                        ) : (
                                                            <HeatPumpIcon />
                                                        )
                                                    }
                                                    titleTypographyProps={{
                                                        variant: "h5",
                                                        fontWeight: "500",
                                                    }}
                                                />
                                                <AppCardContent>
                                                    <VerticalKeyValueList
                                                        kwargs={{
                                                            "Address line 1":
                                                                company.organisationAddress ||
                                                                "The company has not provided an address",
                                                            "Post Code":
                                                                company.organisationPostcode ||
                                                                "The company has not provided a postcode",
                                                            "Contact Number":
                                                                company.organisationContactNumber ||
                                                                "The company has not provided a contact number",
                                                        }}
                                                    />
                                                </AppCardContent>
                                            </AppCard>
                                        ))}
                                    </Stack>
                                    <Stack
                                        paddingLeft={2}
                                        paddingTop={5}
                                        marginTop="auto"
                                        display="flex"
                                        flexWrap="wrap-reverse"
                                    >
                                        <Box display="flex">
                                            <Typography
                                                variant="h6"
                                                color={
                                                    theme.palette.text.secondary
                                                }
                                                align="center"
                                            >
                                                Can&apos;t see the company you
                                                are looking for? Please note we
                                                only return the top 10 results
                                                for you search term. If you
                                                cannot see your result, please
                                                try a more detailed search term
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </>
                            }
                        />
                    }
                />
            </Stack>
        </Box>
    )
}

export default CompanySearch
